import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Tags } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { keywordResultMessage, openQuestionsToDeactivate, keywordDeleteConfirmText, keywordDeletedToast } from "@/lib/keywords";
import { KeywordQuestions, KeywordRow } from "@/components/keywords/KeywordRow";
import { KeywordNotices } from "@/components/keywords/KeywordNotices";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function KeywordsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [toDelete, setToDelete] = useState(null);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [items, setItems] = useState(null);

  const load = () => api.get("/keywords").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const send = async (request) => {
    setBusy(true);
    try {
      const { data } = await request();
      setResult(data);
      if (!data.existing) setWord("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const submit = (e) => {
    e.preventDefault();
    if (word.trim().length >= 2) send(() => api.post("/keywords", { word }));
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-6 sm:py-14 tw-fadeup" data-testid="keywords-page">
      <p className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase">Kennis opbouwen</p>
      <h1 className="mt-2 font-display text-3xl sm:text-5xl font-black text-white leading-none tracking-tight">
        Trefwoorden<span className="text-tw-yellow">.</span>
      </h1>
      <p className="mt-4 text-sm text-tw-muted max-w-md leading-relaxed" data-testid="keywords-intro">
        Typ een onderwerp waar we nog weinig over weten, zoals Kleding of Sleutelkastje. Vocal maakt er een paar vragen van.
      </p>
      <KeywordNotices prefix="keywords-notices" />
      <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-xl" data-testid="keywords-form">
        <input value={word} onChange={(e) => setWord(e.target.value)} maxLength={40} disabled={busy}
          placeholder="Bijvoorbeeld: Kleding" aria-label="Trefwoord" data-testid="keywords-input"
          className="h-12 shrink-0 sm:shrink sm:flex-1 min-w-0 bg-tw-surface border border-tw px-4 text-white placeholder:text-tw-muted focus:outline-none focus:border-tw-yellow transition-colors duration-150" />
        <button type="submit" disabled={busy || word.trim().length < 2} data-testid="keywords-submit"
          className="tw-press h-12 px-5 inline-flex items-center justify-center gap-2 bg-tw-yellow text-black font-bold uppercase tracking-[0.2em] text-[11px] hover:bg-[#E6C200] disabled:opacity-50 transition-colors duration-150">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={2.5} />} Maak vragen
        </button>
      </form>
      {busy && <p className="mt-3 text-sm text-tw-yellow inline-flex items-center gap-2" data-testid="keywords-busy"><Loader2 size={14} className="animate-spin" /> Vragen worden gemaakt…</p>}
      {result && !busy && <KeywordResult kw={result} onRetry={() => send(() => api.post(`/keywords/${result.id}/retry`))} />}
      <KeywordList items={items} onRetry={(id) => send(() => api.post(`/keywords/${id}/retry`))} busy={busy}
        onDelete={isAdmin ? setToDelete : undefined} />
      <DeleteKeywordDialog kw={toDelete} onClose={() => setToDelete(null)}
        onDone={(id) => { setResult((r) => (r?.id === id ? null : r)); load(); }} />
    </div>
  );
}

function DeleteKeywordDialog({ kw, onClose, onDone }) {
  const [deleting, setDeleting] = useState(false);
  const open = openQuestionsToDeactivate(kw);
  const confirm = async (e) => {
    e.preventDefault();
    setDeleting(true);
    try {
      const { data } = await api.delete(`/keywords/${kw.id}`);
      toast.success(keywordDeletedToast(data?.deactivated_questions || 0));
      onClose();
      onDone(kw.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };
  return (
    <AlertDialog open={!!kw} onOpenChange={(v) => { if (!v && !deleting) onClose(); }}>
      <AlertDialogContent className="bg-tw-surface border-tw rounded-none" data-testid="keyword-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Trefwoord verwijderen</AlertDialogTitle>
          <AlertDialogDescription className="text-tw-muted break-words" data-testid="keyword-delete-dialog-text">
            {kw ? keywordDeleteConfirmText(kw.word, open) : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} className="rounded-none" data-testid="keyword-delete-cancel">Annuleren</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} disabled={deleting} data-testid="keyword-delete-confirm"
            className="rounded-none bg-[#FF3B30] text-white hover:bg-[#E0342B]">
            {deleting ? <Loader2 size={14} className="animate-spin" /> : "Verwijderen"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function KeywordResult({ kw, onRetry }) {
  const failed = kw.status === "failed";
  return (
    <section data-testid="keywords-result" data-status={kw.pending ? "pending" : kw.status}
      className={`mt-6 max-w-xl border border-tw border-l-4 ${failed ? "border-l-tw-red" : "border-l-tw-yellow"} bg-tw-surface p-4 tw-fadeup`}>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-tw-muted">{kw.word}</div>
      <p className="mt-1 text-sm text-white font-medium inline-flex items-center gap-2" data-testid="keywords-result-message">
        {kw.pending && <Loader2 size={14} className="animate-spin text-tw-yellow" />}{keywordResultMessage(kw)}
      </p>
      {kw.can_retry && (
        <button type="button" onClick={onRetry} data-testid="keywords-result-retry"
          className="tw-press mt-3 h-9 px-4 border border-tw-yellow text-tw-yellow font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-tw-yellow hover:text-black">
          Opnieuw proberen
        </button>
      )}
      {kw.questions?.length > 0 && <KeywordQuestions kw={kw} prefix="keywords-result" />}
    </section>
  );
}

function KeywordList({ items, onRetry, busy, onDelete }) {
  if (items === null) return <div className="mt-10 text-tw-muted"><Loader2 size={16} className="animate-spin" /></div>;
  return (
    <section className="mt-10" data-testid="keywords-list">
      <h2 className="font-display text-base md:text-lg font-bold text-white flex items-center gap-2 mb-3">
        <Tags size={17} className="text-tw-yellow" /> Wat het team wil weten
      </h2>
      {items.length === 0 ? (
        <p className="text-sm text-tw-muted" data-testid="keywords-empty">Nog geen trefwoorden. Begin hierboven.</p>
      ) : (
        <ul className="grid gap-[3px]">{items.map((kw) => <KeywordRow key={kw.id} kw={kw} onRetry={onRetry} busy={busy} onDelete={onDelete} />)}</ul>
      )}
    </section>
  );
}
